/* ══════════════════════════════════════════════
   SPOTLIGHT SEARCH OVERLAY
   Ported from location-intelligence; groups are Titles + Platforms.
   ══════════════════════════════════════════════ */

/* ── Full-screen backdrop ── */
.overlay {
  display: none;
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0);
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
  align-items: flex-start;
  justify-content: center;
  padding-top: 18vh;
  transition: background 0.25s ease, backdrop-filter 0.25s ease;
}

.overlayActive {
  display: flex;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

/* ── Search card ── */
.card {
  width: 100%;
  max-width: 580px;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  overflow: hidden;
  opacity: 0;
  transform: translateY(8px) scale(0.96);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.cardVisible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* ── Input bar ── */
.bar {
  position: relative;
  background: var(--bg-card);
  border-bottom: 1px solid var(--border-default);
}

/* When loading: hide static border (replaced by animated gradient) */
.barSearching {
  border-bottom-color: transparent;
  box-shadow: 0 0 28px rgba(240, 180, 41, 0.2);
}

/* ── Glow layers (visible only when .barSearching is active) ── */

/* Sharp gradient border ring — sits 1px outside the bar on all sides */
.glowBorder {
  display: block;
  position: absolute;
  inset: -1px;
  border-radius: 14px 14px 1px 1px;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.glowBorder::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 1200px;
  height: 1200px;
  transform: translate(-50%, -50%) rotate(0deg);
  animation: spotlightSpin 1.8s linear infinite;
  background-image: conic-gradient(
    transparent 0%,
    transparent 72%,
    rgba(240, 180, 41, 0.4) 77%,
    #f0b429 82%,
    #fde68a 85%,
    #f0b429 88%,
    rgba(240, 180, 41, 0.4) 93%,
    transparent 100%
  );
}

/* Blurred halo behind the border ring */
.glowOuter {
  display: block;
  position: absolute;
  inset: -4px;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
  filter: blur(10px);
  opacity: 0.65;
}

.glowOuter::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 1200px;
  height: 1200px;
  transform: translate(-50%, -50%) rotate(0deg);
  animation: spotlightSpin 1.8s linear infinite;
  background-image: conic-gradient(
    transparent 0%,
    transparent 65%,
    rgba(240, 180, 41, 0.3) 74%,
    #f0b429 82%,
    #fde68a 86%,
    #f0b429 90%,
    rgba(240, 180, 41, 0.3) 96%,
    transparent 100%
  );
}

/* Interior cover — keeps bg-card background so only the 1px border shows */
.glowCover {
  display: block;
  position: absolute;
  inset: 1px 1px 0;
  border-radius: 13px 13px 0 0;
  background: var(--bg-card);
  pointer-events: none;
  z-index: 1;
}

/* Bar flex content — above all glow layers */
.barContent {
  display: flex;
  align-items: center;
  padding: 0 20px;
  position: relative;
  z-index: 2;
}

@keyframes spotlightSpin {
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.barIcon {
  width: 20px;
  height: 20px;
  color: var(--text-muted);
  flex-shrink: 0;
}

.barInput {
  flex: 1;
  height: 56px;
  background: none;
  border: none;
  outline: none;
  color: var(--text-primary);
  font-size: 17px;
  padding: 0 14px;
  font-family: var(--font-sans);
}

/* The overlay is already an unambiguous focus context, so the global
   :focus-visible gold ring on the input reads as a stray border. Suppress it —
   location-intelligence's search bar shows none. */
.barInput:focus,
.barInput:focus-visible {
  outline: none;
}

.barInput::placeholder {
  color: var(--text-muted);
}

.kbd {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--border-default);
  color: var(--text-muted);
  font-family: inherit;
  flex-shrink: 0;
}

/* ── Results panel ── */
.results {
  max-height: 380px;
  overflow-y: auto;
}

.empty {
  padding: 20px 16px;
  text-align: center;
  color: var(--text-muted);
  font-size: 14px;
}

/* ── Group label ── */
.groupLabel {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  padding: 10px 16px 4px;
}

/* ── Result item ── */
.item {
  all: unset;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-primary);
  transition: background 0.1s;
  width: 100%;
  box-sizing: border-box;
}

.item:hover,
.itemActive {
  background: var(--bg-hover, rgba(255, 255, 255, 0.05));
}

/* ── Title poster ── */
.itemPoster {
  width: 24px;
  height: 36px;
  border-radius: 3px;
  object-fit: cover;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.08);
}

/* ── Platform logo wrapper ── */
.itemLogoWrap {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ── Text block ── */
.itemText {
  flex: 1;
  min-width: 0;
}

.itemName {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  color: var(--text-primary);
}

.itemSub {
  font-size: 12px;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Match highlight ──
   Visible on the dark card: a tinted pill plus a brighter weight on the matched
   run so it reads at a glance, the way location-intelligence's does on its card. */
.mark {
  background: color-mix(in srgb, var(--accent-secondary) 32%, transparent);
  color: var(--text-primary);
  font-weight: var(--weight-semi);
  border-radius: 3px;
  padding: 0 2px;
}

/* ── Light mode overrides ── */
:global([data-theme='light']) .card {
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.12);
}

:global([data-theme='light']) .kbd {
  background: rgba(0, 0, 0, 0.05);
}
